import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Snackbar, useMediaQuery } from '@mui/material';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import DownloadForOfflineRoundedIcon from '@mui/icons-material/DownloadForOfflineRounded';
import { useRegisterSW } from 'virtual:pwa-register/react';

function isStandaloneDisplay() {
    if (typeof window === 'undefined') {
        return false;
    }

    const byDisplayMode = window.matchMedia?.('(display-mode: standalone)')?.matches;
    const byNavigator = window.navigator?.standalone === true;

    return Boolean(byDisplayMode || byNavigator);
}

export default function GlobalPwaControls() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isStandalone, setIsStandalone] = useState(false);
    const [installHelpOpen, setInstallHelpOpen] = useState(false);
    const [installHelpMessage, setInstallHelpMessage] = useState('');
    const isDesktop = useMediaQuery('(min-width:900px)');
    const updatePolicy = String(import.meta.env.VITE_PWA_UPDATE_POLICY || 'force').trim().toLowerCase();
    const forceUpdateDelayMs = Number(import.meta.env.VITE_PWA_FORCE_UPDATE_DELAY_MS || 12000);

    const {
        needRefresh: [needRefresh, setNeedRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        immediate: true,
    });

    useEffect(() => {
        const syncStandalone = () => setIsStandalone(isStandaloneDisplay());

        syncStandalone();

        const onBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setDeferredPrompt(event);
            syncStandalone();
        };

        const onAppInstalled = () => {
            setDeferredPrompt(null);
            setInstallHelpOpen(false);
            syncStandalone();
        };

        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.addEventListener('appinstalled', onAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            window.removeEventListener('appinstalled', onAppInstalled);
        };
    }, []);

    const canShowInstallAction = useMemo(
        () => !isStandalone && Boolean(deferredPrompt),
        [isStandalone, deferredPrompt],
    );

    const canShare = useMemo(
        () => typeof navigator !== 'undefined' && typeof navigator.share === 'function',
        [],
    );

    const handleInstall = useCallback(async () => {
        if (deferredPrompt?.prompt) {
            deferredPrompt.prompt();

            try {
                await deferredPrompt.userChoice;
            } finally {
                setDeferredPrompt(null);
            }

            return;
        }

        // Safari iOS no expone beforeinstallprompt: mostramos guía clara.
        setInstallHelpMessage('Tu navegador no permite instalación automática directa. Usá Compartir > Agregar a pantalla de inicio.');
        setInstallHelpOpen(true);
    }, [deferredPrompt]);

    const handleShareInstallLink = useCallback(async () => {
        if (!canShare) {
            return;
        }

        try {
            await navigator.share({
                title: 'Sistema de Coordinacion de Visitas',
                text: 'Instalá la app en tu dispositivo',
                url: window.location.href,
            });
        } catch {
            // El usuario puede cancelar el share sin que sea un error funcional.
        }
    }, [canShare]);

    const handleCopyInstallLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setInstallHelpMessage('Enlace copiado. Abrilo en tu celular y elegí Agregar a pantalla de inicio.');
            setInstallHelpOpen(true);
        } catch {
            setInstallHelpMessage('No se pudo copiar el enlace automáticamente. Copialo manualmente desde la barra del navegador.');
            setInstallHelpOpen(true);
        }
    }, []);

    const handleUpdate = useCallback(async () => {
        await updateServiceWorker(true);
        setNeedRefresh(false);
    }, [setNeedRefresh, updateServiceWorker]);

    useEffect(() => {
        if (!needRefresh) {
            return undefined;
        }

        if (updatePolicy !== 'force') {
            return undefined;
        }

        // Fuerza actualización automática para acercar el comportamiento al deploy centralizado.
        const timer = window.setTimeout(() => {
            updateServiceWorker(true);
            setNeedRefresh(false);
        }, Number.isFinite(forceUpdateDelayMs) ? forceUpdateDelayMs : 12000);

        return () => window.clearTimeout(timer);
    }, [needRefresh, setNeedRefresh, updatePolicy, forceUpdateDelayMs, updateServiceWorker]);

    return (
        <>
            {canShowInstallAction ? (
                isDesktop ? (
                    <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={handleInstall}
                        startIcon={<DownloadForOfflineRoundedIcon />}
                        sx={{
                            whiteSpace: 'nowrap',
                            minHeight: 34,
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Instalar app
                    </Button>
                ) : (
                    <Box
                        sx={{
                            position: 'fixed',
                            right: { xs: 20, md: 24 },
                            bottom: { xs: 3, md: 10 },
                            zIndex: (theme) => theme.zIndex.tooltip,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<DownloadForOfflineRoundedIcon />}
                            onClick={handleInstall}
                            aria-label="Instalar app"
                            sx={{
                                borderRadius: '25px',
                                minHeight: 32,
                                px: 1.25,
                                py: 0.5,
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                            }}
                        >
                            Instalar app
                        </Button>
                    </Box>
                )
            ) : null}

            <Snackbar
                open={needRefresh}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity="info"
                    variant="filled"
                    icon={<SystemUpdateAltRoundedIcon fontSize="inherit" />}
                    action={(
                        <Button color="inherit" size="small" onClick={handleUpdate}>
                            Actualizar
                        </Button>
                    )}
                    sx={{ width: '100%' }}
                >
                    Nueva versión disponible.
                </Alert>
            </Snackbar>

            <Snackbar
                open={installHelpOpen}
                autoHideDuration={7000}
                onClose={() => setInstallHelpOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setInstallHelpOpen(false)}
                    severity="info"
                    variant="filled"
                    action={(
                        <>
                            {canShare ? (
                                <Button color="inherit" size="small" onClick={handleShareInstallLink}>
                                    Compartir
                                </Button>
                            ) : null}
                            <Button color="inherit" size="small" onClick={handleCopyInstallLink}>
                                Copiar enlace
                            </Button>
                        </>
                    )}
                    sx={{ width: '100%' }}
                >
                    {installHelpMessage || 'Para instalar: abrí el menú del navegador y elegí Agregar a pantalla de inicio.'}
                </Alert>
            </Snackbar>
        </>
    );
}
